import { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { ArrowLeft, Upload, Download, MapPin, Search, Utensils, Clock, Waves } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin view: per-GROUP assignment of Lunch Table, Changing Area, Lunch
 * Time, and Cabana Time. The 5-column CSV template:
 *   group, lunch_table, changing_area, lunch_time, cabana_time
 * All four value fields are free text — whatever the spreadsheet contains
 * is saved verbatim.
 */
export default function CamperLocationsView({ token, onBack }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [savingKey, setSavingKey] = useState("");
  const fileRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const gRes = await axios.get(`${API}/admin/group-times`, { headers });
      setGroups(gRes.data.groups || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load groups");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const updateField = async (groupCode, field, value) => {
    const key = `${groupCode}:${field}`;
    setSavingKey(key);
    try {
      await axios.put(
        `${API}/admin/group-times/${groupCode}`,
        { [field]: value || "" },
        { headers }
      );
      setGroups((prev) =>
        prev.map((g) =>
          g.group_code === groupCode ? { ...g, [field]: value || "" } : g
        )
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingKey("");
    }
  };

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/group-times/upload`, fd, { headers });
      const r = res.data || {};
      toast.success(
        `Updated ${r.updated_groups || 0} group${r.updated_groups === 1 ? "" : "s"}, ` +
        `fanned to ${r.fanned_campers || 0} camper${r.fanned_campers === 1 ? "" : "s"}.`
      );
      if ((r.errors || []).length) {
        console.warn("Group-times upload errors:", r.errors);
        toast.warning(`${r.errors.length} row(s) had errors — see console.`);
      }
      if ((r.not_found_groups || []).length) {
        toast.warning(`Unknown groups: ${r.not_found_groups.join(", ")}`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadTemplate = () => {
    window.open(
      `${API}/admin/group-times/template.csv?token=${encodeURIComponent(token)}`,
      "_blank"
    );
  };

  const filtered = useMemo(() => {
    const lc = q.trim().toLowerCase();
    if (!lc) return groups;
    return groups.filter((g) =>
      (g.group_code || "").toLowerCase().includes(lc)
    );
  }, [groups, q]);

  const filledCount = groups.filter(
    (g) => g.lunch_table && g.changing_area && g.lunch_time && g.cabana_time
  ).length;

  return (
    <div className="space-y-4" data-testid="locations-view">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button variant="ghost" onClick={onBack} data-testid="loc-back" className="-ml-2">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-teal-600" />
          <h2 className="text-xl font-bold">Lunch Table &amp; Changing Area</h2>
        </div>
        <div /> {/* spacer */}
      </div>

      {/* Tools */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Bulk template</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="loc-upload-btn"
          >
            <Upload className="w-3.5 h-3.5 mr-1" />
            {uploading ? "Uploading…" : "Upload CSV / Excel"}
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            className="hidden"
            onChange={(e) => onUpload(e.target.files?.[0])}
            data-testid="loc-upload-input"
          />
          <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="loc-template-btn">
            <Download className="w-3.5 h-3.5 mr-1" />
            Download Template (.csv)
          </Button>
          <p className="text-xs text-muted-foreground mt-2 w-full">
            CSV columns (in order):{" "}
            <code className="text-[11px]">group, lunch_table, changing_area, lunch_time, cabana_time</code>.
            Each row is applied to ALL campers in that group. <strong>cabana_time</strong> is free text — list all 4
            daily periods (e.g. <code className="text-[11px]">10:00-10:30 AM, 11:15-11:45 AM, 1:00-1:30 PM, 2:30-3:00 PM</code>).
            Same schedule applies Mon–Fri (weekends &amp; July 3 not included).
          </p>
        </CardContent>
      </Card>

      {/* Stats + search */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Filter by group code…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-8 h-9"
            data-testid="loc-search-input"
          />
        </div>
        <div className="text-xs text-muted-foreground">
          <span className="font-semibold text-teal-700">{filledCount}</span> /{" "}
          {groups.length} groups fully assigned
        </div>
      </div>

      {/* Per-group table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm" data-testid="loc-table">
              <thead className="bg-muted/50 border-b sticky top-0">
                <tr className="text-left">
                  <th className="p-2 font-semibold w-20">Group</th>
                  <th className="p-2 font-semibold w-12 text-center">#</th>
                  <th className="p-2 font-semibold min-w-[130px]">
                    <Utensils className="w-3.5 h-3.5 inline mr-1 text-amber-600" />
                    Lunch Table
                  </th>
                  <th className="p-2 font-semibold min-w-[150px]">
                    <MapPin className="w-3.5 h-3.5 inline mr-1 text-teal-600" />
                    Changing Area
                  </th>
                  <th className="p-2 font-semibold min-w-[180px]">
                    <Clock className="w-3.5 h-3.5 inline mr-1 text-amber-600" />
                    Lunch Time
                  </th>
                  <th className="p-2 font-semibold min-w-[260px]">
                    <Waves className="w-3.5 h-3.5 inline mr-1 text-sky-600" />
                    Cabana Time <span className="text-[10px] font-normal text-gray-500">(4× daily, Mon–Fri)</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="p-6 text-center text-muted-foreground">
                      Loading groups…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-6 text-center text-muted-foreground">
                      No groups match.
                    </td>
                  </tr>
                ) : (
                  filtered.map((g, i) => {
                    const complete = g.lunch_table && g.changing_area && g.lunch_time && g.cabana_time;
                    return (
                      <tr
                        key={g.group_code}
                        className={i % 2 ? "bg-muted/20" : ""}
                        data-testid={`loc-group-row-${g.group_code}`}
                      >
                        <td className="p-2 font-mono font-bold">
                          {g.group_code}
                          {complete && (
                            <span className="ml-1 inline-block w-2 h-2 rounded-full bg-emerald-500" title="Fully assigned" />
                          )}
                        </td>
                        <td className="p-2 text-center text-muted-foreground">{g.camper_count || 0}</td>
                        <td className="p-2">
                          <Input
                            className="h-8 text-xs"
                            defaultValue={g.lunch_table || ""}
                            placeholder="e.g. Table 7 or 7"
                            onBlur={(e) => {
                              const v = e.target.value.trim();
                              if (v !== (g.lunch_table || "")) {
                                updateField(g.group_code, "lunch_table", v);
                              }
                            }}
                            data-testid={`loc-lunch-table-${g.group_code}`}
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            className="h-8 text-xs"
                            defaultValue={g.changing_area || ""}
                            placeholder="e.g. Cabana 5"
                            onBlur={(e) => {
                              const v = e.target.value.trim();
                              if (v !== (g.changing_area || "")) {
                                updateField(g.group_code, "changing_area", v);
                              }
                            }}
                            data-testid={`loc-changing-area-${g.group_code}`}
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            className="h-8 text-xs"
                            defaultValue={g.lunch_time || ""}
                            placeholder="e.g. 12:30 - 1:00 PM"
                            onBlur={(e) => {
                              const v = e.target.value.trim();
                              if (v !== (g.lunch_time || "")) {
                                updateField(g.group_code, "lunch_time", v);
                              }
                            }}
                            data-testid={`loc-lunch-time-${g.group_code}`}
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            className="h-8 text-xs font-mono"
                            defaultValue={g.cabana_time || ""}
                            placeholder="e.g. 10:00-10:30 AM, 11:15-11:45 AM, 1:00-1:30 PM, 2:30-3:00 PM"
                            onBlur={(e) => {
                              const v = e.target.value.trim();
                              if (v !== (g.cabana_time || "")) {
                                updateField(g.group_code, "cabana_time", v);
                              }
                            }}
                            data-testid={`loc-cabana-time-${g.group_code}`}
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {!loading && (
            <div className="p-2 text-xs text-muted-foreground border-t flex items-center justify-between">
              <span>
                Showing {filtered.length} of {groups.length} groups
                {savingKey && (
                  <span className="ml-2 text-teal-700">Saving…</span>
                )}
              </span>
              <span>Inline edits save on blur. Dropdowns save on select.</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
